/**
 * Formatting helpers. Use these rather than `toLocaleString` at the call site, so
 * dates and money read the same on every screen.
 *
 * Money is a string from the API and stays one. Parsing a decimal into a JavaScript
 * number loses precision at exactly the magnitudes a firm's ledger cares about, and
 * a rounding error in an invoice total is not a display bug.
 */

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(
    date,
  );
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(date);
}

/**
 * "in 3 days", "6 days ago". Used for deadlines, where the relative distance is what
 * the reader is judging — but always beside the absolute date, never instead of it.
 * A limitation date is not something to render only as "in 2 days".
 */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '';
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return '';

  const diffMs = target - Date.now();
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 365 * 86_400_000],
    ['month', 30 * 86_400_000],
    ['day', 86_400_000],
    ['hour', 3_600_000],
    ['minute', 60_000],
  ];

  for (const [unit, ms] of units) {
    if (Math.abs(diffMs) >= ms || unit === 'minute') {
      return formatter.format(Math.round(diffMs / ms), unit);
    }
  }
  return 'now';
}

/** True when a due date has passed. */
export function isPast(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const time = new Date(iso).getTime();
  return !Number.isNaN(time) && time < Date.now();
}

/**
 * Money, from the API's decimal string. Never converted to a float.
 *
 * `currency` is REQUIRED and deliberately has no default. It used to default to `GBP`,
 * which meant a caller who forgot it did not fail -- it rendered somebody else's money
 * with a pound sign, correct-looking and wrong, on a screen a firm reads to decide what
 * it is owed. A missing currency is a caller's mistake and the compiler should say so,
 * because nothing downstream ever will.
 *
 * Records carry their own currency; pass that. For an amount with none -- an estimate on
 * a lead, say -- pass the firm's `baseCurrency` from `useAuth()`.
 */
export function formatMoney(amount: string | null | undefined, currency: string): string {
  if (amount === null || amount === undefined || amount === '') return '—';
  const asNumber = Number(amount);
  if (Number.isNaN(asNumber)) return amount;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(asNumber);
}

/** `awaiting_client` -> `Awaiting client`. For KERNEL-defined statuses only. */
export function humanise(value: string | null | undefined): string {
  if (!value) return '—';
  const spaced = value.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
